import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BUZZLY_FOCUS_OPTIONS,
  CREATIVE_MATERIAL_OPTIONS,
} from "../../../utils/constants";
import {
  PRE_ACTIVATION_FIELD_ADDITIONAL_MEETING_NOTES,
  PRE_ACTIVATION_FIELD_BUZZLY_FOCUS,
  PRE_ACTIVATION_FIELD_CREATIVE_MATERIAL_LEVEL,
  PRE_ACTIVATION_FIELD_CREATIVE_MATERIAL_LOCATION,
  PRE_ACTIVATION_FIELD_MONTHLY_SALES,
  PRE_ACTIVATION_FIELD_SALES_GOAL_60_90,
  PRE_ACTIVATION_HINT_CREATIVE_MATERIAL_LEVEL,
  PRE_ACTIVATION_PLACEHOLDER_ADDITIONAL_MEETING_NOTES,
  PRE_ACTIVATION_PLACEHOLDER_CREATIVE_MATERIAL_LOCATION,
  PRE_ACTIVATION_PLACEHOLDER_MONTHLY_SALES,
  PRE_ACTIVATION_PLACEHOLDER_SALES_GOAL_60_90,
} from "../../../i18n/keysTranslations";
import FieldError from "../components/FieldError";
import FieldBlock from "../components/FieldBlock";
import FieldHint from "../components/FieldHint";
import FieldLabel from "../components/FieldLabel";
import IntakeTextField from "../../../components/Form/IntakeTextField";
import RadioGroup from "../components/RadioGroup";
import ReadOnlySectionGrid from "../components/ReadOnlySectionGrid";

const ObjectivesSection = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  const buzzlyFocusOptions = useMemo(
    () =>
      BUZZLY_FOCUS_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.labelKey),
      })),
    [t]
  );

  const creativeMaterialOptions = useMemo(
    () =>
      CREATIVE_MATERIAL_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.labelKey),
      })),
    [t]
  );

  return (
    <ReadOnlySectionGrid readOnly={readOnly}>
      <FieldBlock>
        <FieldLabel
          htmlFor="monthlySales"
          text={t(PRE_ACTIVATION_FIELD_MONTHLY_SALES)}
          required
        />
        <IntakeTextField
          name="monthlySales"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_MONTHLY_SALES)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="salesGoal60to90"
          text={t(PRE_ACTIVATION_FIELD_SALES_GOAL_60_90)}
          required
        />
        <IntakeTextField
          name="salesGoal60to90"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_SALES_GOAL_60_90)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="buzzlyFocus"
          text={t(PRE_ACTIVATION_FIELD_BUZZLY_FOCUS)}
          required
        />
        <Controller
          name="buzzlyFocus"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <RadioGroup
                name="buzzlyFocus"
                options={buzzlyFocusOptions}
                selectedValue={field.value}
                onChange={field.onChange}
                readOnly={readOnly}
              />
              <FieldError message={fieldState.error?.message} />
            </>
          )}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="creativeMaterialLevel"
          text={t(PRE_ACTIVATION_FIELD_CREATIVE_MATERIAL_LEVEL)}
          required
        />
        <FieldHint text={t(PRE_ACTIVATION_HINT_CREATIVE_MATERIAL_LEVEL)} />
        <Controller
          name="creativeMaterialLevel"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <RadioGroup
                name="creativeMaterialLevel"
                options={creativeMaterialOptions}
                selectedValue={field.value}
                onChange={field.onChange}
                readOnly={readOnly}
              />
              <FieldError message={fieldState.error?.message} />
            </>
          )}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="creativeMaterialLocation"
          text={t(PRE_ACTIVATION_FIELD_CREATIVE_MATERIAL_LOCATION)}
        />
        <IntakeTextField
          name="creativeMaterialLocation"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_CREATIVE_MATERIAL_LOCATION)}
          variant="outlined"
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel
          htmlFor="additionalMeetingNotes"
          text={t(PRE_ACTIVATION_FIELD_ADDITIONAL_MEETING_NOTES)}
        />
        <IntakeTextField
          name="additionalMeetingNotes"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          multiline
          minRows={4}
          readOnly={readOnly}
          placeholder={t(PRE_ACTIVATION_PLACEHOLDER_ADDITIONAL_MEETING_NOTES)}
          variant="outlined"
        />
      </FieldBlock>
    </ReadOnlySectionGrid>
  );
};

export default ObjectivesSection;
